type CognitoApiErrorBody = {
  __type?: unknown;
  message?: unknown;
};

function cognitoEndpoint(authority: string): string {
  const issuer = new URL(authority);
  return `${issuer.protocol}//${issuer.host}/`;
}

async function cognitoRequest<T>(
  authority: string,
  target: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await fetch(cognitoEndpoint(authority), {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });
  if (response.ok) return (await response.json()) as T;
  const error = (await response.json().catch(() => ({}))) as CognitoApiErrorBody;
  throw new Error(typeof error.message === 'string' ? error.message : 'Cognito request failed.');
}

export async function associateSoftwareToken(
  authority: string,
  accessToken: string,
): Promise<string> {
  const result = await cognitoRequest<{ SecretCode?: string }>(
    authority,
    'AssociateSoftwareToken',
    {
      AccessToken: accessToken,
    },
  );
  if (!result.SecretCode) throw new Error('Cognito did not return an authenticator secret.');
  return result.SecretCode;
}

export async function verifySoftwareToken(
  authority: string,
  accessToken: string,
  code: string,
): Promise<void> {
  await cognitoRequest(authority, 'VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: code,
    FriendlyDeviceName: 'quality.vet authenticator',
  });
  await cognitoRequest(authority, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
  });
}

export async function setSoftwareTokenMfa(
  authority: string,
  accessToken: string,
  enabled: boolean,
): Promise<void> {
  await cognitoRequest(authority, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
  });
}

export async function changeCognitoPassword(
  authority: string,
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await cognitoRequest(authority, 'ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

export async function beginEmailChange(
  authority: string,
  accessToken: string,
  email: string,
): Promise<void> {
  await cognitoRequest(authority, 'UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes: [{ Name: 'email', Value: email }],
  });
}

export async function verifyEmailChange(
  authority: string,
  accessToken: string,
  code: string,
): Promise<void> {
  await cognitoRequest(authority, 'VerifyUserAttribute', {
    AccessToken: accessToken,
    AttributeName: 'email',
    Code: code,
  });
}

export type CurrentSessionSummary = {
  authenticatedAt?: Date;
  expiresAt?: Date;
};

export function currentSessionSummary(accessToken: string): CurrentSessionSummary {
  const payload = accessToken.split('.')[1];
  if (!payload) return {};
  try {
    const decoded = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as Record<
      string,
      unknown
    >;
    return {
      ...(typeof decoded.auth_time === 'number'
        ? { authenticatedAt: new Date(decoded.auth_time * 1000) }
        : {}),
      ...(typeof decoded.exp === 'number' ? { expiresAt: new Date(decoded.exp * 1000) } : {}),
    };
  } catch {
    return {};
  }
}

export function hasRecentAuthentication(accessToken: string, maximumAgeMinutes = 10): boolean {
  const { authenticatedAt } = currentSessionSummary(accessToken);
  return Boolean(
    authenticatedAt && Date.now() - authenticatedAt.getTime() <= maximumAgeMinutes * 60_000,
  );
}
